import { coachNameFromPrescribedName } from "../injection-protocol/brand";
import { protocolsForMarket } from "../injection-protocol/markets";
import {
  ALL_INJECTION_PROTOCOLS,
  COACHABLE_NAMES,
} from "../injection-protocol/protocols";
import type { InjectionProtocol } from "../injection-protocol/types";

/**
 * Whether the injection coach belongs on a medication at all, and in what state.
 *
 * Two questions, answered in this order, because the second is meaningless
 * before the first:
 *
 * 1. **Is this something she injects herself?** Route decides, and route decides
 *    first. An intravenous infusion is given in a clinical setting no matter
 *    what else is true of the medication, so it is disqualified before anything
 *    looks at the name. See `docs/features/injection-coach/TECHNIQUE.md`.
 * 2. **Is there an approved procedure for it yet?** Only some medications have
 *    one, and that set grows one medication at a time by design.
 *
 * The distinction earns its keep in the permission flow. A medication with no
 * approved procedure must never reach a camera prompt: asking for a camera the
 * coach has no approved use for is collecting a permission without a purpose,
 * which is the definition of what purpose limitation forbids. So availability
 * gates the disclosure sheet, not just the button's label.
 */

export type CoachAvailability =
  /** No needle, or a needle someone else holds. The entry point does not appear. */
  | { status: "not-an-injection" }
  /**
   * She injects this, but no approved procedure exists for it yet. The entry
   * point appears and says so plainly — and asks for nothing.
   */
  | { status: "no-approved-procedure" }
  /** An approved procedure exists. The full flow is available. */
  | { status: "guided" };

/**
 * Routes a patient administers herself.
 *
 * `injection` is here because the API's `intakeType` sometimes carries the plain
 * word without saying which kind. Intravenous is deliberately absent, as are the
 * oral, vaginal and topical routes — a medication list is full of them and none
 * of them involve a needle.
 */
const SELF_INJECTED_ROUTES = new Set([
  "subcutaneous",
  "intramuscular",
  "injection",
  // French display strings, because `route` reaches this from producers that
  // format it for the screen before handing it over.
  "sous_cutanee",
  "sous_cutane",
  "intramusculaire",
  "injectable",
]);

/**
 * Routes that disqualify outright, whatever else is recorded.
 *
 * Only the ones where someone else holds the needle. An infusion is given in a
 * clinical setting, so a record naming it is a record saying this is not hers to
 * do — and `["intravenous", "injection"]` is a truthful description of exactly
 * that, which must not reach `guided` on the strength of its second half.
 */
const CLINICIAN_ADMINISTERED_ROUTES = new Set([
  "intravenous",
  "intraveineuse",
  "intraveineux",
  "iv",
  "perfusion",
  "voie_intraveineuse",
  "implant",
]);

/**
 * Routes with no needle, in English and in French.
 *
 * These do *not* disqualify on their own, because a molecule can honestly have
 * both: semaglutide is a subcutaneous pen and an oral tablet, and a record
 * listing both is right. They only mean "this route is not the coachable one".
 *
 * Held as two parallel lists rather than one flat array. The flat version had
 * grown a verbatim second copy of its English half — harmless, since a `Set`
 * dedupes, but it hid the thing that actually mattered: the French half was
 * barely a third as long, so a record reading "suppositoire" or "inhalée" fell
 * through to the unknown branch.
 */
const NEEDLE_FREE_ROUTES_EN = [
  "oral",
  "tablet",
  "capsule",
  "liquid",
  "topical",
  "vaginal",
  "intravaginal",
  "rectal",
  "nasal",
  "intranasal",
  "sublingual",
  "inhaled",
  "inhalation",
  "transdermal",
  "patch",
  "drops",
  "eye_drops",
  "suppository",
  "pessary",
  "cream",
  "gel",
  "ointment",
  "spray",
  "lozenge",
];

const NEEDLE_FREE_ROUTES_FR = [
  "orale",
  "voie_orale",
  "comprime",
  "gelule",
  "solution_buvable",
  "sirop",
  "cutanee",
  "voie_cutanee",
  "locale",
  "vaginale",
  "voie_vaginale",
  "ovule",
  "rectale",
  "voie_rectale",
  "suppositoire",
  "nasale",
  "voie_nasale",
  "sublinguale",
  "voie_sublinguale",
  "inhalee",
  "transdermique",
  "patch",
  "gouttes",
  "collyre",
  "creme",
  "pommade",
  "gel",
  "spray",
  "pastille",
];

const NOT_SELF_INJECTED_ROUTES = new Set([
  ...NEEDLE_FREE_ROUTES_EN,
  ...NEEDLE_FREE_ROUTES_FR,
]);

/** Accents off, so "sous-cutanée" and "sous-cutanee" are the same route. */
function foldDiacritics(value: string): string {
  return value.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "");
}

function normalizeRoute(route: string | null | undefined): string | null {
  // Route reaches this from several places and not all of them pass a code:
  // some carry a display string, capitalised or with separators swapped for
  // spaces. Normalising both ways means "Pre-filled syringe" and
  // "pre_filled_syringe" land on the same value.
  if (!route) return null;
  // Accents folded before separators, so "Sous-cutanée", "sous cutanee" and
  // "sous_cutanee" all land on the same value. A producer that formats the route
  // for display is the common case, not the exception.
  const normalized = foldDiacritics(route.trim().toLowerCase()).replaceAll(
    /[\s-]+/g,
    "_",
  );
  return normalized ? normalized : null;
}

export function isSelfInjectedRoute(route: string | null | undefined): boolean {
  const normalized = normalizeRoute(route);
  if (!normalized) return false;
  return SELF_INJECTED_ROUTES.has(normalized);
}

export function resolveCoachAvailability({
  routes,
  medicationName,
  countryCode,
}: {
  /** Every route recorded for the medication; one self-injected route is enough. */
  routes: readonly (string | null | undefined)[];
  medicationName: string;
  /**
   * Where she is, because a procedure that exists is not the same as one she
   * can be offered.
   *
   * Bemfola is the first medication here whose *every* device is
   * market-restricted — EMA-centrally authorised, so the EEA and nowhere else —
   * and until this argument existed a patient in the US with it on her list was
   * shown the entry point, asked for her camera, and handed a walk with no
   * steps in it. Gonal-F never showed the hole because its pen is offered
   * everywhere; only the vial is EEA-only, so there was always something left.
   *
   * Omitting it is the same as not knowing where she is, and that is not the
   * same as "everywhere": a walk whose medicine names markets is then not
   * offered. The asymmetry is `protocolsForMarket`'s and it points this way on
   * purpose — not offering a walk is a real answer, offering another market's
   * is not.
   */
  countryCode?: string | null;
}): CoachAvailability {
  // Accents folded, because "Cétrorélix" and "Cetrorelix" are one prescription
  // and the records reaching this come from clinics, imports and her own typing.
  const normalized = foldDiacritics(medicationName.trim().toLowerCase());
  const hasProtocol =
    COACHABLE_NAMES.some((name) => normalized.includes(foldDiacritics(name))) &&
    // …and at least one of them is sold where she is. Checked by name rather
    // than reusing `resolveCoachProtocols` because that one matches the
    // prescribed name exactly, and this one is deliberately looser: it has to
    // recognise "Gonal-F 900 UI/1,5 ml" typed into a calendar row.
    protocolsForMarket(
      ALL_INJECTION_PROTOCOLS.filter((protocol) =>
        [
          ...protocol.brands.map((brand) => brand.name),
          ...(protocol.aliases ?? []),
        ].some((name) =>
          normalized.includes(foldDiacritics(name.toLowerCase())),
        ),
      ),
      countryCode,
    ).length > 0;

  const recordedRoutes = routes
    .map(normalizeRoute)
    .filter((route): route is string => route !== null);

  // A clinician-administered route decides before anything else looks at the
  // name, which is what this file's contract promises and what the order did
  // not do: `["intravenous", "injection"]` — a truthful description of an
  // infusion — used to reach `guided` on the strength of its second half.
  if (
    recordedRoutes.some((route) => CLINICIAN_ADMINISTERED_ROUTES.has(route))
  ) {
    return { status: "not-an-injection" };
  }

  if (routes.some(isSelfInjectedRoute)) {
    return hasProtocol
      ? { status: "guided" }
      : { status: "no-approved-procedure" };
  }

  // No self-injected route, but a recognised needle-less one: the record is
  // saying plainly that this is not something she injects, and it outranks a
  // name match. Unlike the clinician routes above this does not disqualify on
  // its own — a molecule can honestly be both, as semaglutide is — which is why
  // it is checked after the self-injected branch rather than before it.
  if (recordedRoutes.some((route) => NOT_SELF_INJECTED_ROUTES.has(route))) {
    return { status: "not-an-injection" };
  }

  // Everything else is unknown rather than negative, and the two are not the
  // same. `route` reaches this from several places and not all of them pass a
  // code — some pass a display string, and a display string can be localised —
  // so an unrecognised value means we could not read the record, not that the
  // record said no. Treating it as a refusal hid the coach on every surface that
  // formats the route before handing it over.
  //
  // So the protocol answers instead. That is a sound fallback because a protocol
  // exists only for a medication a patient injects herself, and each one names
  // its own route.
  return hasProtocol ? { status: "guided" } : { status: "not-an-injection" };
}

/**
 * Whether to render an entry point — the factsheet button, the row shortcut.
 *
 * Only where an approved procedure exists. A patient shown an entry point that
 * leads to "not for this medication" has been promised something and then
 * refused it, which is a worse experience than never seeing it, and in a medical
 * surface a broken promise costs more than a missing feature. `no-approved-procedure`
 * is therefore a real state with a real meaning — it is the roadmap, and it is
 * what the tests document — but it renders nothing.
 */
export function offersCoaching(availability: CoachAvailability): boolean {
  return availability.status === "guided";
}

/**
 * Whether this medication may ever reach a camera permission prompt.
 *
 * Only a medication with an approved procedure. Everything else gets the entry
 * point and an explanation, and is asked for nothing.
 */
export function mayRequestCamera(availability: CoachAvailability): boolean {
  return availability.status === "guided";
}

/**
 * An injection she gives herself that the coach has no approved procedure
 * for, yet.
 *
 * Shown as a greyed entry point that says so, rather than nothing: a row
 * with no icon beside rows that have one reads as "this one is not an
 * injection", which is false, and leaves her wondering where the coach
 * went. A pill or an infusion (`not-an-injection`) still shows nothing,
 * because there the absence is the truth.
 */
export function isCoverageGap(availability: CoachAvailability): boolean {
  return availability.status === "no-approved-procedure";
}

/**
 * The procedures this medication has, and the name to speak while walking them.
 *
 * Returns every device variant rather than picking one, because the device is a
 * fact about what the pharmacy dispensed and only she can see it. Ovidrel ships
 * as a pen and as a pre-filled syringe, and the steps genuinely differ — so a
 * surface with more than one has to ask before it can start.
 *
 * The brand comes from the name she was prescribed, never from her locale. See
 * `injection-protocol/brand.ts`.
 */
export function resolveCoachProtocols(
  medicationName: string,
  /**
   * Where she is, for the few procedures that differ by market.
   *
   * Optional, and omitting it returns every procedure — which is the right
   * answer for a caller asking "does this medication have a walk at all" and
   * the wrong one for a caller about to start one. See `protocolsForMarket`.
   */
  countryCode?: string | null,
): {
  brandName: string;
  protocols: readonly InjectionProtocol[];
} | null {
  const normalized = medicationName.trim();
  if (!normalized) return null;

  for (const protocol of ALL_INJECTION_PROTOCOLS) {
    // The trade name if her record carries one, the molecule name if it carries
    // that instead. Never a name the coach chose for her.
    const name = coachNameFromPrescribedName(protocol, normalized);
    if (!name) continue;
    return {
      brandName: name,
      // Every protocol that answers to the same name — the two devices of one
      // trade name, or the two routes of one molecule.
      protocols: protocolsForMarket(
        ALL_INJECTION_PROTOCOLS.filter(
          (candidate) =>
            coachNameFromPrescribedName(candidate, normalized) === name,
        ),
        countryCode,
      ),
    };
  }
  return null;
}
